import {
  Component,
  ContentChildren,
  Input,
  QueryList,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
  ViewEncapsulation,
  ElementRef,
  Renderer2,
  HostListener,
  ChangeDetectorRef,
  HostBinding,
  Inject,
  EventEmitter,
  Output,
} from '@angular/core';

import {
  PgAppFormSectionComponent,
  PgAppFormPageComponent,
} from './pg-app-form-blocks';

import {
  SmallViewportLayoutStrategy,
  PageLayoutStrategy,
  LargeViewportLayoutStrategy,
} from './lib/page-layout-strategy';

import {
  PageTransitionStrategy,
  DefaultPageTransitionStrategy,
} from './lib/page-transition-strategy';
import { RenderedViews } from './types';

import { ActivatedRoute } from '@angular/router';

import { MediaMatcher } from '@angular/cdk/layout';

import { PgAppFormTemplateStyles } from './pg-app-form-template-styles';

import {
  PgBubbleService,
  PgTopbarComponent,
  PgNotificationComponent,
  PgPalette,
  ColorService,
  styleVariables,
  PgBubble,
} from '@progleasing/grit-core';
import { PgTemplatePageHost, PgTemplatePage } from './template-page-host';
import { Platform } from '@angular/cdk/platform';

@Component({
  selector: 'pg-app-form-template, grit-origination-template',
  templateUrl: './pg-app-form-template.component.html',
  styleUrls: ['./pg-app-form-template.component.scss'],
  exportAs: 'pgAppFormTemplate, gritOriginationTemplate',
  providers: [
    // {
    //   provide: PgTemplate,
    //   useExisting: PgAppFormTemplateComponent,
    //   multi: true,
    // },
    {
      provide: PageTransitionStrategy,
      useClass: DefaultPageTransitionStrategy,
    },
    PgBubbleService,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class PgAppFormTemplateComponent implements AfterViewInit {
  @Input()
  key: string = 'lease-origination-template';

  @Input()
  styleOverrides: PgAppFormTemplateStyles;

  @Output()
  pageRendered = new EventEmitter<RenderedViews>();

  @Input()
  particles;
  // Collection of child elements and c omponents for layout manipulation
  @ContentChildren(PgTemplatePageHost, { descendants: true })
  sectionChildren: QueryList<PgTemplatePageHost>;

  @ViewChild(PgTopbarComponent)
  topbar: PgTopbarComponent;

  @ViewChild('sectionWrapper')
  sectionWrapper: ElementRef;
  get sectionWrapperEl(): HTMLElement {
    return this.sectionWrapper.nativeElement;
  }

  @ViewChild('contentWrapper')
  contentWrapper: ElementRef;

  @ViewChild('sectionTitleOutlet', { read: ViewContainerRef })
  sectionTitleOutlet: ViewContainerRef;

  @ViewChild('pageTitleOutlet', { read: ViewContainerRef })
  pageTitleOutlet: ViewContainerRef;

  @ViewChild('pageBodyOutlet', { read: ViewContainerRef })
  pageBodyOutlet: ViewContainerRef;

  @ViewChild('footerSpacer')
  footerSpacer: ElementRef;

  @ViewChild('footer')
  footer: ElementRef;

  @ViewChild('pageTitleParent')
  pageTitleParent: ElementRef;

  @ViewChild('pageBodyParent')
  pageBodyParent: ElementRef;

  @ViewChild('pageNotification')
  pageNotificationChild: PgNotificationComponent;

  // This is set to hidden to hide the whole page on load
  // while letting the reconcilliation process happen
  // then this is set to visible once and is never set back to
  // hidden
  @HostBinding('style.visibility')
  _visibility = 'hidden';

  get section() {
    if (this.currentlyRendered && this.currentlyRendered.section) {
      return this.currentlyRendered.section;
    }
    return null;
  }

  get hostEl(): HTMLElement {
    return this.elementRef.nativeElement;
  }
  // =------

  get queryParams() {
    return this._queryParams;
  }

  protected _queryParams: any;

  get pageKeyHash() {
    return this._pageKeyHash;
  }
  protected _pageKeyHash;
  protected _queryParamsStr;
  public base: string;
  public sections: any[];
  public pages: {
    page: PgAppFormPageComponent;
    section: PgAppFormSectionComponent;
  }[];
  protected _mediaMatchList: MediaQueryList;
  protected _viewportSize: 'sm' | 'lg';
  protected _layoutStrategy: PageLayoutStrategy;
  protected _isExitingTemplate = false;

  public get notificationModel() {
    return this._notificationModel;
  }
  protected _notificationModel: {
    display?: boolean;
    modifier?: 'bold' | null;
    color?: PgPalette;
    type?: 'page';
    actions?: { title: string; action: (event: Event) => void }[];
    action?: 'collapse' | 'dismiss' | null;
    title?: string;
    body?: string;
  } = {};

  get notificationWrapperState() {
    return {
      value: this.pageNotificationChild.display ? 'show' : 'hide',
    };
  }
  currentlyRendered: RenderedViews = {};
  sectionIdentifierHeight;
  sectionOpacity = 1;
  sectionScale = 1;
  navigatingFlag = false;
  get sectionTransform() {
    return `scale(${this.sectionScale})`;
  }

  constructor(
    public mediaMatcher: MediaMatcher,

    public renderer: Renderer2,
    public platform: Platform,
    public pageTransitionStrategy: PageTransitionStrategy,
    public bubbleService: PgBubbleService,
    public activedRoute: ActivatedRoute,
    public colorService: ColorService,
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef
  ) {
    this._mediaMatchList = this.mediaMatcher.matchMedia(
      `(min-width: ${styleVariables.widthBreakpointLg}px)`
    );
    this._setViewportSize(this._mediaMatchList);
    this._mediaMatchList.addListener((m: any) => {
      this._setViewportSize(m);
      this._layoutStrategy.reconcilePageLayout();
    });

    /*
      There is a difficult to reproduce bug which in certain cases
      (usually on server start) When a .NET core project starts up
      an app, the location.pathname doesn't get set properly so
      for the most part we are going to set this.base in the constructor
      but for the rare times the location.pathname is incorrect, it corrects
      itself after a little bit so we are going to just set the base property again
      after a few milliseconds
    */
    this.base = location.pathname;
    setTimeout(() => {
      this.base = location.pathname;
    }, 150);
  }

  ngAfterViewInit() {
    this._parseSectionsAndPages();

    this._parseLocationHash();
    const firstKey = this.pageKeyHash || this.pages[0].page.key;

    this.setPage(firstKey);

    this._initializeBubbles();
    const key = this.currentlyRendered.page.key;

    // We manually set up the gradient transitions after the gradient has rendered.
    // or else they do a transition from a black gradient which looks bad.
    setTimeout(() => {
      const gradientStops = this.hostEl.querySelectorAll(
        '.left-bar-svg-gradient-stop'
      );
      for (let i = 0; i < gradientStops.length; i++) {
        const el = <any>gradientStops[i];
        this.renderer.setStyle(
          el,
          'transition',
          `stop-color 200ms cubic-bezier(0.0, 0.0, 0.2, 1)`
        );
      }
      this._layoutStrategy.reconcilePageLayout();

      // If safari it seems the defs on the svg get
      // messed up on certain routing behaviors
      // so in essence this will jiggle the svg definitions
      // and get them to re-render ... NOTE: I hate safari
      if (this.platform.SAFARI) {
        setTimeout(() => {
          const svg = document.querySelector(
            '#grit-origination-template-background'
          );
          const defs = svg.querySelector('defs');
          svg.removeChild(defs);
          setTimeout(() => {
            svg.appendChild(defs);
          }, 1);
        }, 1);
      }
    }, 100);

    this.changeDetectorRef.detectChanges();
  }

  async showPageNotification(
    title,
    body,
    actions: { title: string; action: (event: any) => void }[],
    collapseAction: 'collapse' | 'dismiss' | null,
    color: PgPalette,
    bold: boolean,
    duration = 0
  ) {
    this._notificationModel = {
      title: title,
      body: body,
      actions: actions,
      action: collapseAction,
      color: color,
      modifier: bold ? 'bold' : null,
    };

    await this.pageNotificationChild.show();
    if (duration) {
      setTimeout(() => {
        this._notificationModel.display = false;
        this.pageNotificationChild.hide();
      }, duration);
    }
  }

  async hidePageNotification() {
    await this.pageNotificationChild.hide();
  }

  getPages(): PgTemplatePage[] {
    return [];
  }

  // Fully animated page transition function
  goToPage(key: string, _recursiveCount = 0) {
    if (_recursiveCount > 64) {
      throw new Error(
        'Circular routing redirect. Check your canActivate functions'
      );
    }
    const targetData = this._getTargetPageData(key);

    const canActivate = this._canActivate(
      this.currentlyRendered.page,
      targetData.page
    );
    if (typeof canActivate === 'boolean') {
      if (!canActivate) {
        return false;
      }
    } else if (typeof canActivate === 'string') {
      return this.goToPage(canActivate, _recursiveCount + 1);
    }
    // this._parseLocationHash();

    const { page, section, pageIndex } = targetData;
    if (this.navigatingFlag) {
      return;
    }
    this.navigatingFlag = true;
    return this.pageTransitionStrategy
      .goToPage(
        this.currentlyRendered,
        {
          section: section,
          page: page,
          sectionTitleTemplate: section.getSectionTitle().childTemplates.first
            .templateRef,
          pageBodyTemplate: page.bodyChild.childTemplates.length
            ? page.bodyChild.childTemplates.first.templateRef
            : null,
          pageTitleTemplate:
            page.titleChild && page.titleChild.childTemplates.length
              ? page.titleChild.childTemplates.first.templateRef
              : null,
        },
        {
          forward: pageIndex > this.currentlyRendered.pageIndex ? true : false,
          sectionTitleOutlet: this.sectionTitleOutlet,
          pageBodyOutlet: this.pageBodyOutlet,
          pageTitleOutlet: this.pageTitleOutlet,
          pageTitleParent: this.pageTitleParent,
          pageBodyParent: this.pageBodyParent,
          layoutStrategy: this._layoutStrategy,
          isLargeViewport: this._viewportSize === 'lg',
        }
      )
      .then((data: RenderedViews) => {
        this.currentlyRendered = data;
        this.currentlyRendered.pageIndex = pageIndex;
        this.pageRendered.emit({ ...this.currentlyRendered });

        if (this._queryParamsStr) {
          location.hash = key + '?' + this._queryParamsStr;
        } else {
          location.hash = key;
        }
        return this._layoutStrategy
          .reconcilePageLayout()
          .then(() => (this._visibility = 'visible'));
      })
      .then(() => {
        this._cacheRenderedSectionHeight();
      })
      .catch(err => {
        console.error(err);
      })
      .then(() => {
        this.navigatingFlag = false;
        this._parseLocationHash();
      });
  }

  // Instant switch, no animation
  setPage(pageKey) {
    const targetPageData = this._getTargetPageData(pageKey);

    const pageBody = targetPageData.page.bodyChild.childTemplates.first;
    const pageTitle = targetPageData.page.titleChild
      ? targetPageData.page.titleChild.childTemplates.first
      : null;
    const sectionTitle = targetPageData.section.getSectionTitle().childTemplates
      .first;

    this.currentlyRendered.pageBodyViewRef = this.pageBodyOutlet.createEmbeddedView(
      pageBody.templateRef
    );
    if (pageTitle) {
      this.currentlyRendered.pageTitleViewRef = this.pageTitleOutlet.createEmbeddedView(
        pageTitle.templateRef
      );
    }
    this.currentlyRendered.sectionTitleViewRef = this.sectionTitleOutlet.createEmbeddedView(
      sectionTitle.templateRef
    );
    this.currentlyRendered.section = targetPageData.section;
    this.currentlyRendered.page = targetPageData.page;
    this.currentlyRendered.pageIndex = targetPageData.pageIndex;

    if (this._queryParamsStr) {
      location.hash = pageKey + '?' + this._queryParamsStr;
    } else {
      location.hash = pageKey;
    }

    this._parseLocationHash();

    setTimeout(() => {
      this.pageRendered.emit(this.currentlyRendered);
      this._cacheRenderedSectionHeight();
      this._layoutStrategy
        .reconcilePageLayout()
        .then(() => (this._visibility = 'visible'));
    }, 5);
  }

  handleBackLinkClick() {
    const page: PgAppFormPageComponent = this.currentlyRendered.page;

    // We want to allow a few options for the consuming app to
    // specify what happens on back link clicked
    // if they pass nothing in to the backAction input
    // we assume they want to pop the history
    // If they pass in a string we assume they are trying to navigate
    // to a specific view in the layout who's key that matches that page
    // Otherwise they are passing in a function to perform an action
    if (page.backAction) {
      if (typeof page.backAction === 'string') {
        this.goToPage(page.backAction);
      } else {
        return page.backAction();
      }
    } else {
      history.back();
    }
  }

  getSnowflakeTransform(bubble: PgBubble) {
    return ` translate(${bubble.xPos}, ${bubble.yPos}) scale(${0.005 *
      bubble.size}) rotate(${bubble.rotation}) `;
  }

  isSectionForegroundDark() {
    return (
      this.section &&
      this.colorService.getContrastColorType(
        this.section.gradientStopColor2 || '#075ED6'
      ) === 'dark'
    );
  }

  getSectionContrastColor() {
    if (this.currentlyRendered && this.currentlyRendered.section) {
      const s: PgAppFormSectionComponent = this.currentlyRendered.section;
      return s.sectionTitleContrastColor;
    }
  }

  private _getTargetPageData(key) {
    let targetPageData: {
      page: PgAppFormPageComponent;
      section: PgAppFormSectionComponent;
      pageIndex?: number;
    };

    this.pages.forEach((p, i) => {
      if (p.page.key === key) {
        targetPageData = p;
        targetPageData.pageIndex = i;
      }
    });
    return targetPageData;
  }

  private _cacheRenderedSectionHeight() {
    this.sectionIdentifierHeight = this.sectionWrapperEl.clientHeight;
  }

  private _setViewportSize(mediaMatcher: MediaQueryList) {
    this._viewportSize = mediaMatcher.matches ? 'lg' : 'sm';
    switch (this._viewportSize) {
      case 'sm':
        this._layoutStrategy = new SmallViewportLayoutStrategy(this);
        break;
      case 'lg':
        this._layoutStrategy = new LargeViewportLayoutStrategy(this);
        break;
    }
  }

  private _parseSectionsAndPages() {
    const sections = [];
    const pages = [];

    this.sectionChildren.forEach(s => {
      s.getPages().forEach(p => {
        pages.push({
          page: p,
          section: s,
        });
      });
      sections.push({ section: s });
    });

    this.sections = sections;
    this.pages = pages;
  }

  private _initializeBubbles() {
    for (let i = 0; i < 30; i++) {
      this.bubbleService.addBubble();
    }
    this.bubbleService.start();
  }

  private _canActivate(
    currentPage: PgAppFormPageComponent,
    nextPage: PgAppFormPageComponent
  ) {
    if (!nextPage.canActivate) {
      return true;
    }
    const result = nextPage.canActivate(currentPage, nextPage);

    return result;
  }

  private _parseLocationHash() {
    const h = location.hash;
    const tokens = h.split('?');

    if (tokens.length === 0) {
      return;
    }

    if (tokens.length > 0) {
      this._pageKeyHash = tokens[0].replace(/#/, '');
    }
    if (tokens.length > 1) {
      this._queryParamsStr = tokens[1];
      this._queryParams = tokens[1].split('&').reduce((acc, cur, index) => {
        const queryTokens = cur.split('=');
        acc[queryTokens[0]] = queryTokens[1];
        return acc;
      }, {});
    }
  }

  @HostListener('window:resize')
  onResize() {
    this._layoutStrategy.reconcilePageLayout();
  }
  @HostListener('window:click', ['$event'])
  addBubble(event: MouseEvent) {
    this.bubbleService.addBubble({ x: event.clientX, y: event.clientY });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    this._parseLocationHash();

    const newKey = this.pageKeyHash;

    if (
      newKey &&
      this.currentlyRendered &&
      this.currentlyRendered.page &&
      newKey !== this.currentlyRendered.page.key
    ) {
      this.goToPage(newKey);
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (
      this._viewportSize === 'lg' &&
      this.sectionOpacity === 1 &&
      this.sectionScale === 1
    ) {
      return;
    }
    if (
      this._viewportSize === 'lg' &&
      (this.sectionOpacity !== 1 || this.sectionScale !== 1)
    ) {
      this.sectionOpacity = 1;
      this.sectionScale = 1;
    } else {
      const maxScaleAdder = 0.04;

      const normalizedScrollY = Math.min(
        this.sectionIdentifierHeight,
        window.scrollY
      );
      const scrollPct = normalizedScrollY / this.sectionIdentifierHeight;
      const inverseOpacityMultiplier = 1 - scrollPct;
      this.sectionOpacity = 1 * inverseOpacityMultiplier;
      this.sectionScale = 1 + maxScaleAdder * scrollPct;
    }
  }
}
